import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, Calendar, DollarSign, Loader2, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function FinancesPage() {
  const [projects, setProjects] = useState([]);
  const [manualInvoices, setManualInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedView, setSelectedView] = useState('month'); // month, quarter, year

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, manualRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: getAuthHeaders() }),
        axios.get(`${API}/all-manual-invoices`, { headers: getAuthHeaders() })
      ]);
      setProjects(projectsRes.data);
      setManualInvoices(manualRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get available years from projects (1 jaar terug, 3 jaar vooruit)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    // 1 jaar terug tot 3 jaar vooruit
    for (let i = -1; i <= 3; i++) {
      years.push(currentYear + i);
    }
    
    return years.sort((a, b) => b - a);
  };

  // Calculate financials by month - using manual invoices for revenue
  const getMonthlyData = () => {
    const monthlyData = {};
    const months = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 
                    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
    
    // Initialize all months
    months.forEach((month, index) => {
      monthlyData[index] = {
        month,
        revenue: 0,
        costs: 0,
        profit: 0,
        projects: 0,
        manualInvoices: 0
      };
    });

    const selectedYearInt = parseInt(selectedYear);

    // Add costs from projects based on project date
    projects.forEach(project => {
      const projectDate = project.start_date || project.created_at;
      if (!projectDate) return;
      
      const date = new Date(projectDate);
      const projectYear = date.getFullYear();
      
      if (projectYear !== selectedYearInt) return;
      
      const monthIndex = date.getMonth();
      const costs = project.total_costs_incl_vat || project.total_costs || 0;
      
      monthlyData[monthIndex].costs += costs;
      monthlyData[monthIndex].projects += 1;
    });

    // Add revenue from manual invoice entries based on invoice_date
    manualInvoices.forEach(entry => {
      const invoiceDate = entry.invoice_date;
      if (!invoiceDate) return;
      
      const date = new Date(invoiceDate);
      const invoiceYear = date.getFullYear();
      
      if (invoiceYear !== selectedYearInt) return;
      
      const monthIndex = date.getMonth();
      monthlyData[monthIndex].revenue += entry.amount || 0;
      monthlyData[monthIndex].manualInvoices += 1;
    });

    // Calculate profit for each month
    Object.values(monthlyData).forEach(data => {
      data.profit = data.revenue - data.costs;
    });

    return Object.values(monthlyData);
  };

  // Calculate quarterly data
  const getQuarterlyData = () => {
    const quarters = [
      { name: 'Q1 (Jan-Mrt)', months: [0, 1, 2] },
      { name: 'Q2 (Apr-Jun)', months: [3, 4, 5] },
      { name: 'Q3 (Jul-Sep)', months: [6, 7, 8] },
      { name: 'Q4 (Okt-Dec)', months: [9, 10, 11] }
    ];

    const selectedYearInt = parseInt(selectedYear);

    return quarters.map(quarter => {
      let revenue = 0, costs = 0, projectCount = 0;

      // Add costs from projects
      projects.forEach(project => {
        const projectDate = project.start_date || project.created_at;
        if (!projectDate) return;
        
        const date = new Date(projectDate);
        const projectYear = date.getFullYear();
        
        if (projectYear !== selectedYearInt) return;
        
        const monthIndex = date.getMonth();
        if (!quarter.months.includes(monthIndex)) return;

        costs += project.total_costs_incl_vat || project.total_costs || 0;
        projectCount += 1;
      });

      // Add revenue from manual invoices
      manualInvoices.forEach(entry => {
        const invoiceDate = entry.invoice_date;
        if (!invoiceDate) return;
        
        const date = new Date(invoiceDate);
        const invoiceYear = date.getFullYear();
        
        if (invoiceYear !== selectedYearInt) return;
        
        const monthIndex = date.getMonth();
        if (!quarter.months.includes(monthIndex)) return;

        revenue += entry.amount || 0;
      });

      return {
        quarter: quarter.name,
        revenue,
        costs,
        profit: revenue - costs,
        projects: projectCount
      };
    });
  };

  // Calculate yearly totals (for selected year)
  const getYearlyData = () => {
    const selectedYearInt = parseInt(selectedYear);
    let yearData = {
      year: selectedYearInt,
      revenue: 0,
      costs: 0,
      profit: 0,
      projects: 0
    };
    
    // Add costs from projects
    projects.forEach(project => {
      const projectDate = project.start_date || project.created_at;
      if (!projectDate) return;
      
      const year = new Date(projectDate).getFullYear();
      
      if (year !== selectedYearInt) return;

      yearData.costs += project.total_costs_incl_vat || project.total_costs || 0;
      yearData.projects += 1;
    });

    // Add revenue from manual invoices
    manualInvoices.forEach(entry => {
      const invoiceDate = entry.invoice_date;
      if (!invoiceDate) return;
      
      const year = new Date(invoiceDate).getFullYear();
      
      if (year !== selectedYearInt) return;

      yearData.revenue += entry.amount || 0;
    });

    yearData.profit = yearData.revenue - yearData.costs;

    return [yearData];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const exportToCSV = () => {
    const data = getData();
    const viewLabel = selectedView === 'month' ? 'Maand' : selectedView === 'quarter' ? 'Kwartaal' : 'Jaar';
    
    // CSV Header
    let csv = `${viewLabel},Omzet,Kosten,Winst,Projecten\n`;
    
    // Data rows
    data.forEach(row => {
      const label = row.month || row.quarter || row.year;
      csv += `${label},${row.revenue.toFixed(2)},${row.costs.toFixed(2)},${row.profit.toFixed(2)},${row.projects}\n`;
    });
    
    // Totals row
    csv += `\nTOTAAL,${totals.revenue.toFixed(2)},${totals.costs.toFixed(2)},${totals.profit.toFixed(2)},${totals.projects}\n`;
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `financien_${selectedView}_${selectedYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Export gedownload!');
  };

  const getData = () => {
    if (selectedView === 'month') return getMonthlyData();
    if (selectedView === 'quarter') return getQuarterlyData();
    return getYearlyData();
  };

  const data = getData();
  const totals = data.reduce((acc, item) => ({
    revenue: acc.revenue + (item.revenue || 0),
    costs: acc.costs + (item.costs || 0),
    profit: acc.profit + (item.profit || 0),
    projects: acc.projects + (item.projects || 0)
  }), { revenue: 0, costs: 0, profit: 0, projects: 0 });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin" size={48} style={{color: '#500000'}} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout showBackToDashboard={true}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl" style={{backgroundColor: '#f5e6e6'}}>
              <TrendingUp size={28} style={{color: '#500000'}} />
            </div>
            <div>
              <h1 className="text-4xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif', color: '#500000'}}>
                Financiën
              </h1>
              <p className="text-sm" style={{color: '#64748B'}}>
                Overzicht van omzet, kosten en winst
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-center flex-wrap">
            <Select value={selectedView} onValueChange={setSelectedView}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">📅 Per Maand</SelectItem>
                <SelectItem value="quarter">📊 Per Kwartaal</SelectItem>
                <SelectItem value="year">🗓️ Boekjaar</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const currentYear = parseInt(selectedYear);
                  const availableYears = getAvailableYears();
                  const currentIndex = availableYears.indexOf(currentYear);
                  if (currentIndex < availableYears.length - 1) {
                    setSelectedYear(availableYears[currentIndex + 1].toString());
                  }
                }}
                disabled={parseInt(selectedYear) <= Math.min(...getAvailableYears())}
                title="Vorig jaar"
              >
                <ChevronLeft size={20} />
              </Button>
              
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Selecteer jaar" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableYears().map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const currentYear = parseInt(selectedYear);
                  const availableYears = getAvailableYears();
                  const currentIndex = availableYears.indexOf(currentYear);
                  if (currentIndex > 0) {
                    setSelectedYear(availableYears[currentIndex - 1].toString());
                  }
                }}
                disabled={parseInt(selectedYear) >= Math.max(...getAvailableYears())}
                title="Volgend jaar"
              >
                <ChevronRight size={20} />
              </Button>
            </div>

            <Button
              onClick={exportToCSV}
              variant="outline"
              className="flex items-center gap-2"
              style={{color: '#500000', borderColor: '#500000'}}
            >
              <Download size={18} />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{color: '#64748B'}}>Totale Omzet</p>
                  <p className="text-2xl font-bold mt-2" style={{color: '#3a190b'}}>
                    {formatCurrency(totals.revenue)}
                  </p>
                </div>
                <div className="p-3 rounded-full" style={{backgroundColor: '#f5e6e6'}}>
                  <DollarSign size={24} style={{color: '#500000'}} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{color: '#64748B'}}>Totale Kosten</p>
                  <p className="text-2xl font-bold mt-2" style={{color: '#DC2626'}}>
                    {formatCurrency(totals.costs)}
                  </p>
                </div>
                <div className="p-3 rounded-full" style={{backgroundColor: '#FEE2E2'}}>
                  <TrendingUp size={24} style={{color: '#DC2626', transform: 'rotate(180deg)'}} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{color: '#64748B'}}>Totale Winst</p>
                  <p className="text-2xl font-bold mt-2" style={{color: totals.profit >= 0 ? '#059669' : '#DC2626'}}>
                    {formatCurrency(totals.profit)}
                  </p>
                </div>
                <div className="p-3 rounded-full" style={{backgroundColor: totals.profit >= 0 ? '#D1FAE5' : '#FEE2E2'}}>
                  <TrendingUp size={24} style={{color: totals.profit >= 0 ? '#059669' : '#DC2626'}} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{color: '#64748B'}}>Projecten</p>
                  <p className="text-2xl font-bold mt-2" style={{color: '#3a190b'}}>
                    {totals.projects}
                  </p>
                </div>
                <div className="p-3 rounded-full" style={{backgroundColor: '#f5e6e6'}}>
                  <Calendar size={24} style={{color: '#500000'}} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Table */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#3a190b'}}>
              Gedetailleerd Overzicht
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2" style={{borderColor: '#E5E7EB'}}>
                    <th className="text-left py-3 px-4 font-semibold" style={{color: '#64748B'}}>
                      {selectedView === 'month' ? 'Maand' : selectedView === 'quarter' ? 'Kwartaal' : 'Jaar'}
                    </th>
                    <th className="text-right py-3 px-4 font-semibold" style={{color: '#64748B'}}>Omzet</th>
                    <th className="text-right py-3 px-4 font-semibold" style={{color: '#64748B'}}>Kosten</th>
                    <th className="text-right py-3 px-4 font-semibold" style={{color: '#64748B'}}>Winst</th>
                    <th className="text-right py-3 px-4 font-semibold" style={{color: '#64748B'}}>Projecten</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50" style={{borderColor: '#F3F4F6'}}>
                      <td className="py-3 px-4 font-medium" style={{color: '#3a190b'}}>
                        {row.month || row.quarter || row.year}
                      </td>
                      <td className="text-right py-3 px-4" style={{color: '#3a190b'}}>
                        {formatCurrency(row.revenue)}
                      </td>
                      <td className="text-right py-3 px-4" style={{color: '#DC2626'}}>
                        {formatCurrency(row.costs)}
                      </td>
                      <td className="text-right py-3 px-4 font-semibold" style={{color: row.profit >= 0 ? '#059669' : '#DC2626'}}>
                        {formatCurrency(row.profit)}
                      </td>
                      <td className="text-right py-3 px-4" style={{color: '#64748B'}}>
                        {row.projects}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-bold" style={{borderColor: '#E5E7EB'}}>
                    <td className="py-3 px-4" style={{color: '#3a190b'}}>TOTAAL</td>
                    <td className="text-right py-3 px-4" style={{color: '#3a190b'}}>
                      {formatCurrency(totals.revenue)}
                    </td>
                    <td className="text-right py-3 px-4" style={{color: '#DC2626'}}>
                      {formatCurrency(totals.costs)}
                    </td>
                    <td className="text-right py-3 px-4" style={{color: totals.profit >= 0 ? '#059669' : '#DC2626'}}>
                      {formatCurrency(totals.profit)}
                    </td>
                    <td className="text-right py-3 px-4" style={{color: '#64748B'}}>
                      {totals.projects}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
